import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import CircularProgress from "@mui/material/CircularProgress";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import AdminIcon from "./adminIcons";
import { ADMIN_COLORS, ADMIN_FONT, ADMIN_ICON_SIZE, ADMIN_TYPE } from "./adminUi";

var DEFAULT_PAGE_SIZES = [20, 50, 100];
var HEAD_ROW_HEIGHT = 52;
var BODY_ROW_HEIGHT = 52;
var PAGER_CONTROL_HEIGHT = 34;
var BODY_TEXT = { fontFamily: ADMIN_FONT, fontSize: ADMIN_TYPE.body.fontSize, lineHeight: ADMIN_TYPE.body.lineHeight };

/** Page numbers with "…" gaps, e.g. 1 2 3 … 8 (0-based in, 1-based labels out). */
export function pageItems(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, function (_, i) {
      return i;
    });
  }
  var items = [0];
  var start = Math.max(1, current - 1);
  var end = Math.min(total - 2, current + 1);
  if (current <= 2) end = 3;
  if (current >= total - 3) start = total - 4;
  if (start > 1) items.push("gap-start");
  for (var i = start; i <= end; i++) items.push(i);
  if (end < total - 2) items.push("gap-end");
  items.push(total - 1);
  return items;
}

function compareText(a, b) {
  return String(a || "").localeCompare(String(b || ""), undefined, { sensitivity: "base" });
}

function PagerCaret(props) {
  return (
    <AdminIcon
      name="CaretDown"
      size={16}
      className={props.className}
      sx={{ right: "10px !important", top: "calc(50% - 8px) !important", color: ADMIN_COLORS.pageText }}
    />
  );
}

function PageButton(props) {
  return (
    <ButtonBase
      onClick={props.onClick}
      disabled={props.disabled}
      aria-label={props.label}
      aria-current={props.active ? "page" : undefined}
      sx={Object.assign(
        {
          minWidth: PAGER_CONTROL_HEIGHT,
          height: PAGER_CONTROL_HEIGHT,
          px: props.icon ? "6px" : "4px",
          borderRadius: "6px",
          border: "1px solid " + (props.active ? ADMIN_COLORS.brand : ADMIN_COLORS.line),
          color: props.active ? ADMIN_COLORS.brand : ADMIN_COLORS.pageText,
          bgcolor: ADMIN_COLORS.page,
          "&:hover": { borderColor: ADMIN_COLORS.brand },
          "&.Mui-disabled": { opacity: 0.45 },
        },
        BODY_TEXT
      )}
    >
      {props.children}
    </ButtonBase>
  );
}

/**
 * Figma data table with client-side paging.
 * Columns: { field, header, width, minWidth, sortable, muted, render(row, index) }.
 * Only columns marked `sortable` get the sort control.
 */
function AdminTable(props) {
  var pageSizes = props.pageSizeOptions || DEFAULT_PAGE_SIZES;
  var [pageSize, setPageSize] = useState(pageSizes[0]);
  var [page, setPage] = useState(0);
  var [sort, setSort] = useState({ field: "", dir: "asc" });
  var rows = props.rows || [];

  var sortedRows = useMemo(
    function () {
      if (!sort.field) return rows;
      var copy = rows.slice();
      copy.sort(function (a, b) {
        var result = compareText(a[sort.field], b[sort.field]);
        return sort.dir === "asc" ? result : -result;
      });
      return copy;
    },
    [rows, sort]
  );

  var total = sortedRows.length;
  var pageCount = Math.max(1, Math.ceil(total / pageSize));

  useEffect(
    function () {
      setPage(0);
    },
    [props.resetKey, pageSize]
  );

  useEffect(
    function () {
      if (page > pageCount - 1) setPage(pageCount - 1);
    },
    [page, pageCount]
  );

  var startIndex = page * pageSize;
  var visible = sortedRows.slice(startIndex, startIndex + pageSize);

  function toggleSort(field) {
    setSort(function (prev) {
      if (prev.field !== field) return { field: field, dir: "asc" };
      return { field: field, dir: prev.dir === "asc" ? "desc" : "asc" };
    });
  }

  var headCellSx = {
    px: "8px",
    py: 0,
    height: HEAD_ROW_HEIGHT,
    boxSizing: "border-box",
    bgcolor: ADMIN_COLORS.page,
    fontFamily: ADMIN_FONT,
    fontSize: ADMIN_TYPE.tableHead.fontSize,
    lineHeight: ADMIN_TYPE.tableHead.lineHeight,
    fontWeight: ADMIN_TYPE.tableHead.fontWeight,
    color: ADMIN_COLORS.label,
    textAlign: "left",
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
    zIndex: 1,
    borderBottom: "1px solid " + ADMIN_COLORS.lineSoft,
  };

  return (
    <Box
      sx={{
        border: "1px solid " + ADMIN_COLORS.line,
        borderRadius: "6px",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        minHeight: props.fill ? 260 : 0,
        flex: props.fill ? 1 : "0 0 auto",
        bgcolor: ADMIN_COLORS.card,
      }}
    >
      <Box sx={{ overflow: "auto", flex: 1, minHeight: 0 }}>
        <Box
          component="table"
          sx={{ width: "100%", minWidth: props.minWidth || 1180, borderCollapse: "separate", borderSpacing: 0 }}
        >
          <thead>
            <tr>
              {props.columns.map(function (col, index) {
                var active = sort.field === col.field;
                return (
                  <Box
                    component="th"
                    key={col.field}
                    scope="col"
                    aria-sort={col.sortable && active ? (sort.dir === "asc" ? "ascending" : "descending") : undefined}
                    sx={Object.assign({}, headCellSx, {
                      width: col.width,
                      minWidth: col.minWidth,
                      pl: index === 0 ? "16px" : "8px",
                    })}
                  >
                    {col.sortable ? (
                      <ButtonBase
                        onClick={function () {
                          toggleSort(col.field);
                        }}
                        aria-label={"Sort by " + col.header}
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                          font: "inherit",
                          color: active ? ADMIN_COLORS.brand : "inherit",
                          borderRadius: "4px",
                        }}
                      >
                        {col.header}
                        <AdminIcon
                          name="ArrowsDownUp"
                          size={16}
                          sx={{ transform: active && sort.dir === "desc" ? "scaleY(-1)" : "none" }}
                        />
                      </ButtonBase>
                    ) : (
                      col.header
                    )}
                  </Box>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {props.loading ? (
              <tr>
                <Box component="td" colSpan={props.columns.length} sx={{ py: 8, textAlign: "center" }}>
                  <CircularProgress size={26} sx={{ color: ADMIN_COLORS.brand }} />
                </Box>
              </tr>
            ) : !visible.length ? (
              <tr>
                <Box
                  component="td"
                  colSpan={props.columns.length}
                  sx={Object.assign({ py: 8, textAlign: "center", color: ADMIN_COLORS.labelMuted }, BODY_TEXT)}
                >
                  {props.emptyText || "No records found"}
                </Box>
              </tr>
            ) : (
              visible.map(function (row, rowIndex) {
                return (
                  <Box
                    component="tr"
                    key={row.id}
                    sx={{ "&:hover td": { bgcolor: "#F7F8FA" } }}
                  >
                    {props.columns.map(function (col, index) {
                      return (
                        <Box
                          component="td"
                          key={col.field}
                          sx={{
                            p: "8px",
                            pl: index === 0 ? "16px" : "8px",
                            height: BODY_ROW_HEIGHT,
                            boxSizing: "border-box",
                            fontFamily: ADMIN_FONT,
                            fontSize: ADMIN_TYPE.tableCell.fontSize,
                            lineHeight: ADMIN_TYPE.tableCell.lineHeight,
                            color: col.muted ? ADMIN_COLORS.labelSecondary : ADMIN_COLORS.label,
                            borderBottom: "1px solid " + ADMIN_COLORS.lineSoft,
                            whiteSpace: col.wrap ? "normal" : "nowrap",
                            maxWidth: col.maxWidth,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            bgcolor: ADMIN_COLORS.card,
                          }}
                        >
                          {col.render ? col.render(row, startIndex + rowIndex) : row[col.field] || "—"}
                        </Box>
                      );
                    })}
                  </Box>
                );
              })
            )}
          </tbody>
        </Box>
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          p: "12px",
          bgcolor: ADMIN_COLORS.page,
          borderTop: "1px solid " + ADMIN_COLORS.lineSoft,
        }}
      >
        <Typography sx={Object.assign({ color: ADMIN_COLORS.pageText }, BODY_TEXT)}>
          {total
            ? "Showing: " + (startIndex + 1) + " - " + Math.min(startIndex + pageSize, total) + " of " + total
            : "Showing: 0 of 0"}
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "24px" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <Typography sx={Object.assign({ color: ADMIN_COLORS.pageText }, BODY_TEXT)}>
              Rows per page
            </Typography>
            <Select
              size="small"
              value={pageSize}
              onChange={function (e) {
                setPageSize(Number(e.target.value));
              }}
              inputProps={{ "aria-label": "Rows per page" }}
              IconComponent={PagerCaret}
              sx={{
                width: 84,
                height: PAGER_CONTROL_HEIGHT,
                bgcolor: ADMIN_COLORS.card,
                borderRadius: "6px",
                fontFamily: ADMIN_FONT,
                fontSize: ADMIN_TYPE.body.fontSize,
                "& .MuiSelect-select": { py: "4px", pl: "12px", pr: "28px !important" },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: ADMIN_COLORS.line },
              }}
            >
              {pageSizes.map(function (size) {
                return (
                  <MenuItem key={size} value={size} sx={{ fontSize: ADMIN_TYPE.body.fontSize }}>
                    {size}
                  </MenuItem>
                );
              })}
            </Select>
          </Box>
          <Box component="nav" aria-label="Pagination" sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <PageButton
              icon
              label="Previous page"
              disabled={page === 0}
              onClick={function () {
                setPage(page - 1);
              }}
            >
              <AdminIcon name="ChevronRight" size={ADMIN_ICON_SIZE.button} sx={{ transform: "rotate(180deg)" }} />
            </PageButton>
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
              {pageItems(page, pageCount).map(function (item) {
                if (typeof item === "string") {
                  return (
                    <Box
                      key={item}
                      sx={Object.assign({ minWidth: PAGER_CONTROL_HEIGHT, textAlign: "center", color: ADMIN_COLORS.pageText }, BODY_TEXT)}
                    >
                      ...
                    </Box>
                  );
                }
                return (
                  <PageButton
                    key={item}
                    active={item === page}
                    label={"Page " + (item + 1)}
                    onClick={function () {
                      setPage(item);
                    }}
                  >
                    {item + 1}
                  </PageButton>
                );
              })}
            </Box>
            <PageButton
              icon
              label="Next page"
              disabled={page >= pageCount - 1}
              onClick={function () {
                setPage(page + 1);
              }}
            >
              <AdminIcon name="ChevronRight" size={ADMIN_ICON_SIZE.button} />
            </PageButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default AdminTable;
